import { isDefined } from '@library/guards'
import { Validator } from 'fluentvalidation-ts'

import type { IntervalValue } from './Interval'

type ValidationRule<TValue, TModel> = {
  predicate: (value: TValue, model: TModel) => boolean
  message: string | ((value: TValue, model: TModel) => string)
}

type RangeConstraint = {
  value: number
  inclusive: boolean
}

const DEFAULT_MIN_CONSTRAINT: RangeConstraint = {
  value: -Infinity,
  inclusive: false,
}

const DEFAULT_MAX_CONSTRAINT: RangeConstraint = {
  value: Infinity,
  inclusive: false,
}

function getIntervalRuleFor<K extends keyof IntervalValue>(
  type: K,
  allowEquality: boolean,
): ValidationRule<IntervalValue[K], IntervalValue> {
  const isLowerCheck = type === 'from'
  const oppositeType = isLowerCheck ? 'to' : 'from'
  const additionalText = allowEquality ? ' или равно' : ''

  return {
    predicate: (value, model) => {
      const oppositeValue = model[oppositeType]
      if (!isDefined(value) || !isDefined(oppositeValue)) return true

      const isEqual = allowEquality && value === oppositeValue
      const isRightOrder = isLowerCheck === value < oppositeValue

      return isRightOrder || isEqual
    },
    message: `От должно быть меньше${additionalText} до`,
  }
}

function getGlobalRangeConstraintFor<K extends keyof IntervalValue>(
  type: K,
  constraint: RangeConstraint,
): ValidationRule<IntervalValue[K], IntervalValue> {
  const isLowerCheck = type === 'from'
  const isConstraintFinite = isFinite(constraint.value)
  const startingText = isLowerCheck ? 'От' : 'До'
  const comparacent = isLowerCheck ? 'больше' : 'меньше'
  const additionalText = constraint.inclusive && isConstraintFinite ? ' или равно ' : ' '
  const endText = isConstraintFinite
    ? constraint.value
    : `${isLowerCheck ? 'минус' : ''} бесконечности`

  return {
    predicate: value => {
      if (!isDefined(value)) return true

      const isEqual = constraint.inclusive && value === constraint.value
      const isRightOrder = isLowerCheck !== value < constraint.value

      return isRightOrder || isEqual
    },
    message: `${startingText} должно быть ${comparacent}${additionalText}${endText}`,
  }
}

export class IntervalValidation extends Validator<IntervalValue> {
  constructor(
    globalMin: RangeConstraint = DEFAULT_MIN_CONSTRAINT,
    globalMax: RangeConstraint = DEFAULT_MAX_CONSTRAINT,
    allowEquality: boolean,
  ) {
    super()

    this.ruleFor('from')
      .must(getIntervalRuleFor('from', allowEquality))
      .must(getGlobalRangeConstraintFor('from', globalMin))

    this.ruleFor('to')
      .must(getIntervalRuleFor('to', allowEquality))
      .must(getGlobalRangeConstraintFor('to', globalMax))
  }
}
